import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { FC } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import { useNavigate } from "react-router-dom";
import { AdminRoutes } from "config/routes";
import { GuideLayout } from "./GuideLayout";

type AuthGuidePageProps = {
  markdown: string;
  backPath?: string;
  backLabel?: string;
  actionPath?: string;
  actionLabel?: string;
};

export const AuthGuidePage: FC<AuthGuidePageProps> = ({
  markdown,
  backPath = AdminRoutes.Login,
  backLabel = "Back to Sign In",
  actionPath,
  actionLabel,
}) => {
  const navigate = useNavigate();

  const markdownComponents: Components = {
    img: ({ src, alt }) => (
      <Box component="img" src={src ?? ""} alt={alt ?? ""} />
    ),
  };

  return (
    <GuideLayout>
      <Box
        sx={{
          width: "100%",
          "& h1": { fontSize: "1.25rem", fontWeight: 600, mt: 0, mb: 2 },
          "& h2": { fontSize: "1.1rem", fontWeight: 600, mt: 3, mb: 1.5 },
          "& h3": { fontSize: "1rem", fontWeight: 600, mt: 2, mb: 1 },
          "& p, & li": { fontSize: "0.95rem", lineHeight: 1.6 },
          "& ul, & ol": { pl: 2.5, my: 1 },
          "& hr": { my: 3, border: 0, borderTop: "1px solid", borderColor: "divider" },
          "& strong": { fontWeight: 600 },
          "& code": {
            fontFamily: "monospace",
            fontSize: "0.875rem",
            bgcolor: "grey.100",
            px: 0.5,
            py: 0.25,
            borderRadius: 0.5,
          },
          "& img": {
            display: "block",
            maxWidth: "100%",
            height: "auto",
            my: 2,
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
          },
        }}
      >
        <ReactMarkdown components={markdownComponents}>
          {markdown}
        </ReactMarkdown>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 3 }}>
        {actionPath && actionLabel && (
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={() => navigate(actionPath)}
          >
            {actionLabel}
          </Button>
        )}
        <Button
          fullWidth
          variant={actionPath ? "outlined" : "contained"}
          color="primary"
          onClick={() => navigate(backPath)}
        >
          {backLabel}
        </Button>
      </Box>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 2, textAlign: "center" }}
      >
        Need more help? Contact your VetNebula program administrator.
      </Typography>
    </GuideLayout>
  );
};
